import styled from "styled-components";
import type { IColors } from "@swiggy-private/dls-web-common/dist/components/types";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { DINE_CASH_TOOLTIP_LIST_WIDTH, DINE_CASH_TOOLTIP_WRAPPER_WIDTH } from "@dineout-payment-success-ui/constants";
import { SPLIT_BILL_TEST_IDS } from "@swiggy-private/payment-test-ids";

const TOAST_CONTAINER_BOTTOM_VALUE = "140px";

export const Wrapper = styled.div(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    maxHeight: "62vh",
    paddingTop: theme.spacing.xLarge,
    position: "relative",
}));

export const BillAmountSection = styled.div(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing.xSmall,
    marginBottom: theme.spacing.x18,
}));

export const TabsContainer = styled.div(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    borderBottom: `1px solid ${theme.colors[theme.colorMode].border_Tertiary}`,
    margin: `0 ${theme.spacing.medium}`,
}));

export const Tab = styled.div(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: `0 ${theme.spacing.xSmall}`,
    paddingBottom: theme.spacing.small,
    position: "relative",
}));

export const TabLabelRow = styled.div(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing.xxSmall,
}));

export const TabUnderline = styled.div(({ theme }) => ({
    position: "absolute",
    bottom: "-1px",
    left: 0,
    right: 0,
    height: "3px",
    backgroundColor: theme.colors[theme.colorMode].primary,
    borderTopLeftRadius: theme.cornerRadius.xSmall,
    borderTopRightRadius: theme.cornerRadius.xSmall,
}));

export const GuestsSection = styled.div(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.medium,
    margin: `0 ${theme.spacing.medium}`,
    flex: 1,
    overflow: "hidden",
}));

export const GuestsHeader = styled.div({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
});

export const SplitEquallyButton = styled.button(({ theme }) => ({
    background: theme.colors[theme.colorMode].background_Primary,
    border: `1px solid ${theme.colors[theme.colorMode].border_Subtle_Secondary}`,
    borderRadius: theme.cornerRadius.xSmall,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.xxSmall,
    padding: theme.spacing.x3,
}));

export const GuestsList = styled.div(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.large,
    overflowY: "auto",
    overflowX: "hidden",
    flex: 1,
    padding: `${theme.spacing.large} 0`,
    "&::-webkit-scrollbar": {
        display: "none",
    },
}));

export const AddMoreGuests = styled.button(({ theme }) => ({
    background: "transparent",
    border: "none",
    padding: `${theme.spacing.xSmall} 0`,
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.xSmall,
}));

export const DineCashSection = styled.div<{ isEnabled: boolean }>(({ theme, isEnabled }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing.x7,
    opacity: isEnabled ? 1 : 0.5,
}));

export const DineCashLeft = styled.div(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.xSmall,
}));

export const DineCashInfoAnchor = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
`;

/** DLS ToolTip root class — used for outside-click detection and style overrides. */
export const DINE_CASH_DLS_TOOLTIP_CLASS = SPLIT_BILL_TEST_IDS.DINECASH_DLS_TOOLTIP;

export const DineCashInfoIconTrigger = styled.div(({ theme }) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: theme.spacing.xxxSmall,
    cursor: "pointer",
    lineHeight: 0,
    borderRadius: theme.cornerRadius.rounded,
    "&:focus-visible": {
        outline: `2px solid ${theme.colors[theme.colorMode].border_Secondary}`,
        outlineOffset: 2,
    },
}));

/** Positions DLS `ToolTip` above the info icon (same pattern as cart rewards widget). */
export const DineCashTooltipWrap = styled.div(({ theme }) => ({
    position: "absolute",
    left: "50%",
    bottom: `calc(100% + ${theme.spacing.xSmall})`,
    transform: "translateX(-50%)",
    width: DINE_CASH_TOOLTIP_WRAPPER_WIDTH,
}));

export const DineCashTooltipList = styled.ul(({ theme }) => ({
    listStyle: "disc",
    marginLeft: theme.spacing.small,
    width: DINE_CASH_TOOLTIP_LIST_WIDTH,
    padding: theme.spacing.x5,
    fontSize: theme.typography["V2.body3.neutral.regular"].fontSize,
    borderRadius: theme.cornerRadius.xSmall,
    color: theme.colors[theme.colorMode].text_inverse_Highest_Emphasis,
}));

export const DineCashTooltipListItem = styled.li(({ theme }) => ({
    marginBottom: theme.spacing.xxSmall,

    "&:last-child": {
        marginBottom: 0,
    },
}));

export const DineCashRight = styled.div(({ theme }) => ({
    display: "flex",
    gap: theme.spacing.xSmall,
}));

export const ButtonContainer = styled.div(({ theme }) => ({
    position: "sticky",
    bottom: 0,
    padding: theme.spacing.medium,
    paddingTop: theme.spacing.x10,
    borderTopLeftRadius: theme.cornerRadius.small,
    borderTopRightRadius: theme.cornerRadius.small,
    boxShadow: theme.elevation[theme.colorMode].liftedNegative.boxShadow,
    backgroundColor: theme.colors[theme.colorMode].background_Primary,

    "@supports (padding-bottom: var(--sab))": {
        paddingBottom: `calc(${theme.spacing.medium} + var(--sab, 0px))`,
    },
}));

export const ToastContainer = styled.div(({ theme }) => ({
    position: "absolute",
    bottom: TOAST_CONTAINER_BOTTOM_VALUE,
    left: 0,
    right: 0,
    margin: `0 ${theme.spacing.medium}`,
    zIndex: theme.elevation[theme.colorMode].lifted.zIndex,

    "@supports (padding-bottom: var(--sab))": {
        bottom: `calc(${TOAST_CONTAINER_BOTTOM_VALUE} + var(--sab, 0px))`,
    },
}));

export const StatusWrapper = styled.div(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    padding: theme.spacing.medium,
    textAlign: "center",
}));

export const StatusImageWrap = styled.div(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: theme.spacing.medium,
}));

export const StatusMessageBlock = styled.div<{ hasWarningCallout?: boolean }>(({ theme, hasWarningCallout }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing.xSmall,
    padding: `0 ${theme.spacing.xxLarge}`,
    marginTop: theme.spacing.large,
    marginBottom: hasWarningCallout ? theme.spacing.medium : theme.spacing.xxLarge,
}));

export const StatusWarningCallout = styled.div<{ backgroundColor: keyof IColors }>(({ theme, backgroundColor }) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing.xSmall,
    width: "100%",
    padding: theme.spacing.small,
    borderRadius: theme.cornerRadius.medium,
    backgroundColor: theme.colors[theme.colorMode][backgroundColor],
    textAlign: "left",
    marginTop: theme.spacing.small,
    marginBottom: theme.spacing.medium,
}));

export const StatusWarningCalloutIconWrap = styled.div({
    display: "inline-flex",
    flexShrink: 0,
    lineHeight: 0,
    marginTop: "1px",
});

export const StatusWarningCalloutText = styled(Text)({
    maxWidth: "100%",
    flex: 1,
});

export const StatusCtaWrap = styled.div({
    width: "100%",
    paddingBottom: 0,

    "@supports (padding-bottom: var(--sab))": {
        paddingBottom: "var(--sab, 0px)",
    },
});

export const ShimmerContainer = styled.div<{ height: string; width: string; borderRadius: string }>(
    ({ theme, height, width, borderRadius }) => ({
        width: width,
        height: height,
        borderRadius: borderRadius,
        backgroundColor: theme.colors[theme.colorMode].background_Secondary,
    }),
);

export const StatusShimmerSubtextGroup = styled.div(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.xSmall,
    alignItems: "center",
    marginTop: theme.spacing.xSmall,
}));
